import { BzDateFns, IsoDateString } from '@breezy/shared'
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useInterval } from 'react-use'
import { getConfig } from '../config'
import { rumEvent } from '../utils/rum'

export type ConnectivityStatus = 'healthy' | 'degraded' | 'offline'

export type PlatformVersionPayload = {
  platformVersion: string
  appVersion: string
  latestBreakingVersion: string
}

type ConnectivityContextValue = {
  status: ConnectivityStatus
  lastSuccessAt?: IsoDateString
  lastSuccessAtMs?: number
  lastCheckedAt?: IsoDateString
  consecutiveFailures: number
  isChecking: boolean
  error?: Error
  refresh: () => Promise<void>
  lastPayload?: PlatformVersionPayload
}

const ConnectivityContext = createContext<ConnectivityContextValue | null>(null)

const VISIBLE_INTERVAL_MS = 15_000
const HIDDEN_INTERVAL_MS = 60_000
const UNHEALTHY_VISIBLE_INTERVAL_MS = 5_000
const TIMEOUT_MS = 4_000
const OFFLINE_AFTER_FAILURES = 3
const OFFLINE_AFTER_SECONDS = 45

export const ConnectivityProvider = ({ children }: { children: React.ReactNode }) => {
  const apiUrl = getConfig().apiUrl

  const [status, setStatus] = useState<ConnectivityStatus>('healthy')
  const [lastSuccessAt, setLastSuccessAt] = useState<IsoDateString | undefined>()
  const [lastSuccessAtMs, setLastSuccessAtMs] = useState<number | undefined>()
  const [lastCheckedAt, setLastCheckedAt] = useState<IsoDateString | undefined>()
  const [consecutiveFailures, setConsecutiveFailures] = useState(0)
  const [isChecking, setIsChecking] = useState(false)
  const [error, setError] = useState<Error | undefined>()
  const [isHidden, setIsHidden] = useState<boolean>(document.hidden)
  const [lastPayload, setLastPayload] = useState<PlatformVersionPayload | undefined>()

  const inFlightRef = useRef<Promise<void> | null>(null)
  const firstFailureAtRef = useRef<IsoDateString | undefined>()
  const previousStatusRef = useRef<ConnectivityStatus | undefined>('healthy')

  const refresh = useCallback(async () => {
    if (inFlightRef.current) {
      return inFlightRef.current
    }

    setIsChecking(true)

    const controller = new AbortController()
    const timeoutId = window.setTimeout(() => controller.abort(), TIMEOUT_MS)
    const startedAt = BzDateFns.nowISOString()

    const run = (async () => {
      try {
        const response = await fetch(`${apiUrl}/platform-version`, { signal: controller.signal })

        if (!response.ok) {
          throw new Error(`Heartbeat failed with status ${response.status}`)
        }

        const payload = (await response.json()) as PlatformVersionPayload
        const nowIso = BzDateFns.nowISOString()
        setLastPayload(payload)
        setLastSuccessAt(nowIso)
        setLastSuccessAtMs(Date.now())
        setLastCheckedAt(startedAt)
        setConsecutiveFailures(0)
        setStatus('healthy')
        setError(undefined)
        firstFailureAtRef.current = undefined
      } catch (err) {
        const currentError = err instanceof Error ? err : new Error('Unknown connectivity error')

        setError(currentError)
        setLastCheckedAt(startedAt)

        setConsecutiveFailures(prev => {
          const next = prev + 1

          if (!firstFailureAtRef.current) {
            firstFailureAtRef.current = startedAt
          }

          const failureWindowSeconds = firstFailureAtRef.current
            ? BzDateFns.differenceInSeconds(
                BzDateFns.parseISO(BzDateFns.nowISOString(), BzDateFns.UTC),
                BzDateFns.parseISO(firstFailureAtRef.current, BzDateFns.UTC),
              )
            : 0

          const shouldMarkOffline = next >= OFFLINE_AFTER_FAILURES || failureWindowSeconds >= OFFLINE_AFTER_SECONDS

          setStatus(shouldMarkOffline ? 'offline' : 'degraded')

          return next
        })
      } finally {
        window.clearTimeout(timeoutId)
        setIsChecking(false)
      }
    })()

    inFlightRef.current = run
    await run
    inFlightRef.current = null
  }, [apiUrl])

  useEffect(() => {
    void refresh()
  }, [refresh])

  useEffect(() => {
    if (!previousStatusRef.current || previousStatusRef.current === status) {
      previousStatusRef.current = status
      return
    }

    rumEvent('connectivity_status_change', {
      from: previousStatusRef.current,
      to: status,
      consecutiveFailures,
      isHidden,
      lastSuccessAt,
      lastCheckedAt,
      errorName: error?.name,
      errorMessage: error?.message,
      payloadAppVersion: lastPayload?.appVersion,
    })

    previousStatusRef.current = status
  }, [
    consecutiveFailures,
    error?.message,
    error?.name,
    isHidden,
    lastCheckedAt,
    lastPayload?.appVersion,
    lastSuccessAt,
    status,
  ])

  useEffect(() => {
    const onVisibilityChange = () => {
      const hidden = document.hidden
      setIsHidden(hidden)

      if (!hidden) {
        void refresh()
      }
    }

    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => document.removeEventListener('visibilitychange', onVisibilityChange)
  }, [refresh])

  const intervalMs = isHidden
    ? HIDDEN_INTERVAL_MS
    : status === 'healthy'
      ? VISIBLE_INTERVAL_MS
      : UNHEALTHY_VISIBLE_INTERVAL_MS

  useInterval(() => {
    void refresh()
  }, intervalMs)

  const value = useMemo<ConnectivityContextValue>(
    () => ({
      status,
      lastSuccessAt,
      lastSuccessAtMs,
      lastCheckedAt,
      consecutiveFailures,
      isChecking,
      error,
      refresh,
      lastPayload,
    }),
    [
      consecutiveFailures,
      error,
      isChecking,
      lastCheckedAt,
      lastPayload,
      lastSuccessAt,
      lastSuccessAtMs,
      refresh,
      status,
    ],
  )

  return <ConnectivityContext.Provider value={value}>{children}</ConnectivityContext.Provider>
}

export const useConnectivity = () => {
  const context = useContext(ConnectivityContext)

  if (!context) {
    throw new Error('useConnectivity must be used within a ConnectivityProvider')
  }

  return context
}

export const useOptionalConnectivity = () => useContext(ConnectivityContext)
